import { lazyRequire } from '../commons/lazyRequire';
import type * as utils from '../utils';
import type { WindowUtils } from '../player/utils/windowUtils';

export interface CropArea {
    left: number;
    top: number;
    width: number;
    height: number;
}

interface ImagePart {
    position: {
        left: number;
        top: number;
    };
    size: {
        width: number;
        height: number;
    };
    image: string;
}

/**
 * Loads an image from a base64-encoded string.
 * 
 * @param {string} base64Data - The base64-encoded image data (without data URL prefix)
 * @returns {Promise<Image>} A promise that resolves to the loaded image object
 */
export async function loadImageFromBase64(base64Data: string) {
    const { loadImage } = await lazyRequire('canvas');
    const buffer = Buffer.from(base64Data, 'base64');
    return await loadImage(buffer);
}

/**
 * Gets the dimensions (width and height) of a base64-encoded image.
 * 
 * @param {string} base64Screenshot - The base64-encoded image data
 * @returns {Promise<{width: number, height: number}>} A promise that resolves to an object containing the image dimensions
 */
export async function getImageDimensions(base64Screenshot: string): Promise<{ width: number; height: number }> {
    const image = await loadImageFromBase64(base64Screenshot);
    return { width: image.width, height: image.height };
}

/**
 * Calculates the scale factor between a screenshot and the Appium viewport size.
 * This is useful for handling high-DPI displays where the screenshot resolution
 * differs from the logical viewport size.
 * 
 * @param {string} base64Screenshot - The base64-encoded screenshot data
 * @param {{width: number, height: number}} appiumSize - The logical viewport size from Appium
 * @returns {Promise<{scaleFactor: number, width: number, height: number}>} A promise that resolves to an object containing the scale factor and actual image dimensions
 */
export async function getImageScaleFactor(
    base64Screenshot: string,
    appiumSize: { width: number; height: number },
): Promise<{ scaleFactor: number; width: number; height: number }> {
    const { width, height } = await getImageDimensions(base64Screenshot);
    return { scaleFactor: width / appiumSize.width, width, height };
}

/**
 * Crops a specific area from a base64-encoded image.
 * 
 * @param {string} base64Screenshot - The base64-encoded source image
 * @param {CropArea} area - The rectangular area to crop from the image
 * @returns {Promise<string>} A promise that resolves to a data URL of the cropped image (PNG format)
 */
export async function cropImage(base64Screenshot: string, area: CropArea): Promise<string> {
    const {createCanvas, Image} = await lazyRequire('canvas');
    const canvas = createCanvas(area.width, area.height);
    const ctx = canvas.getContext('2d');
    
    const img = new Image();
    img.src = `data:image/png;base64,${base64Screenshot}`;
    await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = reject;
    });
    
    // Draw the cropped portion: source (x,y,w,h) -> destination (0,0,w,h)
    ctx.drawImage(img, area.left, area.top, area.width, area.height, 0, 0, area.width, area.height);
    return canvas.toDataURL('image/png');
}




/**
 * Stitches multiple image parts together into a single full-size image.
 * This is typically used for reconstructing a full page screenshot from multiple viewport captures.
 * 
 * @param {Object} fullSize - The dimensions of the final stitched image
 * @param {number} fullSize.width - The width of the final image
 * @param {number} fullSize.height - The height of the final image
 * @param {ImagePart[]} parts - Array of image parts to stitch together, each containing position, size, and image data
 * @returns {Promise<string>} A promise that resolves to a data URL of the stitched image (PNG format)
 */
export async function stitchImages(fullSize: utils.AwaitedReturnType<WindowUtils['getFullPageSize']>, parts: ImagePart[]) { 
    const MIME_PNG = 'image/png';
    const {createCanvas, Image} = await lazyRequire('canvas');
    const fulImage = createCanvas(fullSize.width, fullSize.height);
    const ctx = fulImage.getContext('2d');
    for (const part of parts) {
        const imageRegExMatch = part.image.match(/^data:([^;]*);base64,(.*)$/);
        const mimeType = imageRegExMatch![1];
        const base64Data = imageRegExMatch![2];
        const img = new Image();
        img.src = `data:${mimeType};base64,${base64Data}`;   
        await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = reject;
        });
        ctx.drawImage(img, part.position.left, part.position.top, part.size.width, part.size.height);
    }
    const base64 =  fulImage.toDataURL(MIME_PNG);
    return base64;
};


